/**
 * Hook para buscar contadores de atividades pendentes por tipo
 * Utiliza RPC otimizada para performance máxima (única query com GROUP BY)
 * 
 * @version 1.3.0
 * RELEASE: 2026-02-10 v1.3.0 - Adicionado filtros de período (startDate/endDate)
 * RELEASE: 2026-02-09 v1.2.0 - Adicionado suporte a filtros: workspace, seller, user, status, período
 * RELEASE: 2026-02-09 v1.1.0 - Adicionado suporte a filtro onlyMine (role-based visibility)
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ActivityType } from '@/hooks/useCrmDealActivities';
import type { ActivityStatusFilter } from '@/components/crm/filters/CrmActivityStatusFilter';

// Logger para o módulo
const logger = createLogger('ActivitiesFeedCounts');

/**
 * Interface dos contadores por tipo de atividade
 */
export interface ActivityTypeCounts {
  all: number;      // Soma de todos os tipos
  note: number;
  call: number;
  email: number;
  meeting: number;
  task: number;
  system: number;  // v1.4.0: total de atividades is_system=TRUE no período
  comments: number; // Menções não lidas do usuário atual
}

/**
 * Tipos de atividade que são contados (apenas manuais, não sistema)
 */
const COUNTABLE_ACTIVITY_TYPES: ActivityType[] = ['note', 'call', 'email', 'meeting', 'task'];

/**
 * Opções do hook de contadores
 */
export interface ActivitiesFeedCountsOptions {
  /** Workspace do negócio (null = todos) */
  workspaceId?: string | null;
  /** Vendedor do negócio (null = todos) */
  sellerId?: string | null;
  /** Responsável pela atividade (null = todos) */
  performedByUserId?: string | null;
  /** Status da atividade: 'all', 'open', 'completed' */
  status?: ActivityStatusFilter;
  /** v1.3.0: Data início do período (null = sem filtro) */
  startDate?: Date | null;
  /** v1.3.0: Data fim do período (null = sem filtro) */
  endDate?: Date | null;
}

/**
 * Hook para buscar contadores de atividades pendentes
 * Chama a RPC get_activities_feed_counts
 * 
 * @param options - Filtros de workspace, seller, user e status
 */
export function useActivitiesFeedCounts(options?: ActivitiesFeedCountsOptions) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  
  // Extrai opções com defaults
  const workspaceId = options?.workspaceId ?? null;
  const sellerId = options?.sellerId ?? null;
  const performedByUserId = options?.performedByUserId ?? null;
  const status = options?.status ?? 'all';
  // v1.3.0: Filtros de período
  const startDate = options?.startDate ?? null;
  const endDate = options?.endDate ?? null;

  // Query para contadores de atividades por tipo
  const activitiesQuery = useQuery({
    queryKey: ['activities-feed-counts', currentTenant?.id, workspaceId, sellerId, performedByUserId, status, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // v1.3.0: Passa parâmetros de filtro incluindo período
      // Nota: Cast necessário até regeneração dos tipos do Supabase
      const { data, error } = await supabase
        .rpc('get_activities_feed_counts', {
          p_workspace_id: workspaceId,
          p_seller_id: sellerId,
          p_user_id: performedByUserId,
          p_status: status === 'all' ? null : status,
          p_start_date: startDate ? startDate.toISOString().split('T')[0] : null,
          p_end_date: endDate ? endDate.toISOString().split('T')[0] : null,
        } as Record<string, unknown>)
        .returns<{ activity_type: string; pending_count: number }[]>();

      if (error) {
        logger.error('Erro ao buscar contadores de atividades:', error.message);
        throw error;
      }

      return data ?? [];
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Query para contagem de menções não lidas (aba Comentários)
  const unreadMentionsQuery = useQuery({
    queryKey: ['unread-mentions-count', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return 0;

      const { data, error } = await supabase.rpc('get_unread_mentions_count');

      if (error) {
        logger.error('Erro ao buscar menções não lidas:', error.message);
        throw error;
      }

      return (data as number) ?? 0;
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  // Monta o objeto de contadores combinando as duas queries
  const counts: ActivityTypeCounts = {
    all: 0,
    note: 0,
    call: 0,
    email: 0,
    meeting: 0,
    task: 0,
    system: 0,
    comments: unreadMentionsQuery.data ?? 0,
  };

  // Popula contadores de atividades
  // v1.4.0: linha sentinela '__system__' alimenta counts.system (não soma em all)
  if (activitiesQuery.data) {
    for (const row of activitiesQuery.data) {
      if (row.activity_type === '__system__') {
        counts.system = row.pending_count;
        continue;
      }
      const type = row.activity_type as ActivityType;
      if (COUNTABLE_ACTIVITY_TYPES.includes(type)) {
        counts[type] = row.pending_count;
        counts.all += row.pending_count;
      }
    }
  }

  logger.debug('Contadores de atividades carregados:', counts);

  /**
   * Invalida o cache dos contadores (atividades e menções)
   * Chamado quando atividades são criadas, completadas ou deletadas
   */
  const invalidateCounts = () => {
    queryClient.invalidateQueries({ queryKey: ['activities-feed-counts'] });
    queryClient.invalidateQueries({ queryKey: ['unread-mentions-count'] });
  };

  return {
    counts,
    isLoading: activitiesQuery.isLoading || unreadMentionsQuery.isLoading,
    isError: activitiesQuery.isError || unreadMentionsQuery.isError,
    error: activitiesQuery.error || unreadMentionsQuery.error,
    refetch: () => {
      activitiesQuery.refetch();
      unreadMentionsQuery.refetch();
    },
    invalidateCounts,
  };
}
